import {
    EventClient,
    EventClientProvider,
    FormattedEvent,
} from '@eb/event-renderer';
import { sdkRequest } from '@eb/http';
import React, { useEffect, useState } from 'react';
import { SettingsContext } from '../../app';
import { EnhancedAppProps } from '../../types';
import {
    getShareComponentProps,
    ShareProps,
} from '../ShareEvent/ShareEventRoot';
import { dynaMount } from '../utils/mountComponent';
import {
    CollectionResponse,
    excludeDuplicatedEvents,
    filterEvents,
    getOrganizerRelatedEventsFromDestination,
    getRelatedEvents,
    NUMBER_OF_MORE_EVENTS_TO_SHOW,
} from './api';
import MoreEventsFrom from './MoreEventsFrom';
import { MoreRelatedCollections } from './MoreRelatedCollections';
import OtherEventsYouMayLike from './OtherEventsYouMayLike';
import {
    EventsFromCollectionResponse,
    transformCollectionEvent,
} from './transform';
import { useExpiredEvents } from './useExpiredEvents';

const eventClient = new EventClient();

export interface RelatedEventsProps {
    eventId: string;
    isAuthenticated: boolean;
    shareComponentProps: ShareProps;
    events?: FormattedEvent[];
    organizerId: string;
    creatorCollections?: CollectionResponse[];
}

export type RelatedEventsChildrenProps = Omit<
    RelatedEventsProps,
    'organizerId' | 'eventId'
> & { isSingleCreatorCollection?: boolean; collectionUrl?: string };

export const RELATED_EVENTS_ID = 'listings-root__related-events';
export const MORE_EVENTS_FROM_ID =
    'listings-root__more-events-from-this-organizer';
export const RELATED_COLLECTIONS_ID =
    'listings-root__collections-related-to-this-event';

export const getRelatedEventsProps = (
    props: EnhancedAppProps,
): RelatedEventsProps => {
    const {
        event: { id: eventId },
        user: { isAuthenticated },
        organizer: { id: organizerId },
        creatorCollections,
    } = props;

    const shareComponentProps = getShareComponentProps(props);

    return {
        eventId,
        isAuthenticated,
        shareComponentProps,
        organizerId,
        creatorCollections,
    };
};

export const OtherEventsYouMayLikeMount = (
    props: RelatedEventsChildrenProps,
): JSX.Element | React.ReactPortal | null =>
    dynaMount(
        {
            Component: OtherEventsYouMayLike,
            containerId: RELATED_EVENTS_ID,
        },
        { ...props },
    );

export const MoreRelatedCollectionsMount = (props: {
    eventId: string;
}): JSX.Element | React.ReactPortal | null =>
    dynaMount(
        {
            Component: (props) => (
                <MoreRelatedCollections eventId={props.eventId} />
            ),
            containerId: RELATED_COLLECTIONS_ID,
        },
        { ...props },
    );

export const MoreEventsFromMount = (
    props: RelatedEventsChildrenProps,
): JSX.Element | React.ReactPortal | null =>
    dynaMount(
        {
            Component: MoreEventsFrom,
            containerId: MORE_EVENTS_FROM_ID,
        },
        { ...props },
    );

export const RelatedEventsWrapper = (
    props: RelatedEventsProps,
): JSX.Element | null => {
    const {
        featureFlags: { launchLinkToCreatorCollectionsOnListing = false } = {},
    } = React.useContext(SettingsContext);

    const { organizerId, eventId, creatorCollections } = props;

    const isSingleCreatorCollection = creatorCollections?.length === 1;
    const shouldShowCreatorCollectionEvents =
        isSingleCreatorCollection && launchLinkToCreatorCollectionsOnListing;
    const collectionUrl =
        (creatorCollections && creatorCollections[0]?.relative_url) ||
        undefined;

    const relatedEvents = useRelatedEvents(
        organizerId,
        eventId,
        launchLinkToCreatorCollectionsOnListing,
        creatorCollections?.length,
        creatorCollections && creatorCollections[0]?.id,
    );

    useExpiredEvents();

    return (
        <EventClientProvider client={eventClient}>
            <div data-testid="related-events-wrapper">
                {relatedEvents.eventsSameOrganizer && (
                    <MoreEventsFromMount
                        {...props}
                        events={relatedEvents.eventsSameOrganizer || []}
                    />
                )}
                {relatedEvents.eventsSameCollection && (
                    <MoreEventsFromMount
                        {...props}
                        events={relatedEvents.eventsSameCollection || []}
                        isSingleCreatorCollection={
                            shouldShowCreatorCollectionEvents
                        }
                        collectionUrl={collectionUrl}
                    />
                )}
                {relatedEvents.eventsOtherOrganizers && (
                    <OtherEventsYouMayLikeMount
                        {...props}
                        events={relatedEvents.eventsOtherOrganizers}
                    />
                )}
                {creatorCollections && creatorCollections?.length > 1 && (
                    <MoreRelatedCollectionsMount eventId={props.eventId} />
                )}
            </div>
        </EventClientProvider>
    );
};

const useRelatedEvents = (
    organizerId: string,
    eventId: string,
    launchLinkToCreatorCollectionsOnListing: boolean,
    collectionsLength?: number,
    collectionId?: string,
) => {
    const [eventsSameOrganizer, setEventsSameOrganizer] = useState<
        FormattedEvent[]
    >();
    const [eventsSameCollection, setEventsSameCollection] = useState<
        FormattedEvent[]
    >();
    const [eventsOtherOrganizers, setEventsOtherOrganizers] = useState<
        FormattedEvent[]
    >();

    useEffect(() => {
        const fetchEventsSameOrganizer = () => {
            getOrganizerRelatedEventsFromDestination(organizerId, eventId)
                .then((response) => {
                    if (response) {
                        const { events } = response;
                        setEventsSameOrganizer(events);
                        return events;
                    }
                })
                .then((eventsSameOrganizer) => {
                    fetchEventsOtherOrganizers(eventsSameOrganizer);
                });
        };

        const fetchEventsSameCollection = () => {
            sdkRequest(
                `/collections/${collectionId}/events/public?time_filter=current_future`,
            )
                .then((response: EventsFromCollectionResponse) => {
                    const filteredEvents = filterEvents(
                        NUMBER_OF_MORE_EVENTS_TO_SHOW,
                        response.events,
                        eventId,
                    );
                    const transformedEvents = filteredEvents.map((event) => {
                        return transformCollectionEvent(event);
                    });
                    setEventsSameCollection(transformedEvents);
                    return transformedEvents;
                })
                .then((eventsSameCollection: FormattedEvent[]) => {
                    fetchEventsOtherOrganizers(eventsSameCollection);
                });
        };

        const fetchEventsOtherOrganizers = (eventsFrom?: FormattedEvent[]) => {
            const _getRelatedEventsOtherOrganizersApi = async (
                eventId: string,
                eventsFromIds: string[] = [],
            ) => {
                const relatedEvents = await getRelatedEvents(eventId);
                const filteredEvents =
                    eventsFromIds.length > 0
                        ? excludeDuplicatedEvents(
                              relatedEvents.events,
                              eventsFromIds,
                          )
                        : relatedEvents.events;

                return filteredEvents;
            };

            const shouldShowRelatedEventsLive = document.getElementById(
                RELATED_EVENTS_ID,
            );

            const eventsFromIds: string[] | undefined = eventsFrom?.map(
                (event) => event.id,
            );

            if (shouldShowRelatedEventsLive) {
                _getRelatedEventsOtherOrganizersApi(
                    eventId,
                    eventsFromIds,
                ).then((filteredEvents) => {
                    setEventsOtherOrganizers(filteredEvents);
                });
            }
        };

        if (collectionsLength === 0) {
            fetchEventsSameOrganizer();
        } else if (
            collectionsLength === 1 &&
            launchLinkToCreatorCollectionsOnListing
        ) {
            fetchEventsSameCollection();
        } else if (
            collectionsLength &&
            collectionsLength >= 1 &&
            launchLinkToCreatorCollectionsOnListing
        ) {
            fetchEventsOtherOrganizers();
        }
    }, [
        collectionId,
        collectionsLength,
        eventId,
        organizerId,
        launchLinkToCreatorCollectionsOnListing,
    ]);

    return { eventsSameOrganizer, eventsSameCollection, eventsOtherOrganizers };
};

const RelatedEventsRoot = (props: EnhancedAppProps): JSX.Element | null => {
    // TODO: We should replace this part to use
    // enable_more_events_from_this_organizer_on_listings FF from the mako template
    // https://github.com/eventbrite/core/blob/master/django/templates/event/event_page/listing/_event_listing_body.html#L30
    const enableMoreEventsFrom = document.getElementById(MORE_EVENTS_FROM_ID);

    if (!enableMoreEventsFrom) {
        return <div data-testid="loading-state"></div>;
    }

    const relatedEventsProps = getRelatedEventsProps(props);
    return <RelatedEventsWrapper {...relatedEventsProps} />;
};

export default RelatedEventsRoot;
